import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';

import {
  IconAlertTriangleFilled,
  IconCircle,
  IconCircleCheck,
} from '@tabler/icons-react';
import { IconCircleCheckDisabled, IconCircleDisabled, Play } from 'icons';
import { useTranslation } from 'react-i18next';

import { ContentStep, PlanAssignment } from '@cfa/pathway-fe-shared/xp';
import { Typography } from '@cfa/react-components';

import { getStepAvailable, getStepResource } from '@/utils/contentStep';
import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import { BrightcovePlayer } from '@/components/BrightcovePlayer';

import { LocationState } from '@/types/types';

import Constants from 'constants/index';
import { itemByLanguage } from 'utils/language';

import { tempProcessVideoSrc } from '../DocumentPreview/utils';

interface PlanItemBrightcoveVideoProps {
  disabled?: boolean;
  isActive: boolean;
  isComplete: boolean;
  onClick: (step: ContentStep) => void;
  onCompleteToggle: () => void;
  statuses?: PlanAssignment[];
  step: ContentStep;
  users: string[];
}

const PlanItemBrightcoveVideo: React.FC<PlanItemBrightcoveVideoProps> = ({
  disabled,
  isActive,
  isComplete,
  onClick,
  onCompleteToggle,
  statuses,
  step,
  users,
}) => {
  const { t } = useTranslation();
  const location = useLocation() as { state?: LocationState };
  const [isCompleted, setIsCompleted] = useState(isComplete);
  const [videoUrl, setVideoUrl] = useState<string>();

  const routeMatches = useRouteMatcher();
  const isMyPlansTab = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingMyPlansPath,
  );

  const userStatuses = statuses?.filter(status =>
    users.find(id => status.userId === id),
  );

  let activeVideoUserStatuses = userStatuses?.filter(userStatus =>
    userStatus.steps?.find(stepStatus => stepStatus.stepId === step.id),
  );
  if (!activeVideoUserStatuses?.length) {
    activeVideoUserStatuses = userStatuses;
  }

  const currentUserStatus = activeVideoUserStatuses?.find(
    userStatus => userStatus.userId === users[0],
  );

  const videoStatus = currentUserStatus?.steps?.find(
    stepStatus => stepStatus.stepId === step.id,
  );

  // Use `duration` (API field) not `stepDuration` (legacy normalized field).
  // See PATHWAY-2935 for context.
  const stepDuration = Math.round((videoStatus?.duration ?? 0) / 60);

  const videoReference = getStepResource(step)?.references?.at(0)?.urls.at(0);

  const xpDataResourceUrl = videoReference?.contentUrl;

  useEffect(() => {
    setIsCompleted(isComplete);
  }, [isComplete]);

  useEffect(() => {
    (async () => {
      if (!xpDataResourceUrl || videoUrl || !isActive) {
        return;
      }
      const response = await fetch(xpDataResourceUrl, {
        credentials: 'include',
      });
      if (response.ok) {
        const html = await response.text();
        const htmlUrl = html.slice(html.indexOf('https:')).trim();
        setVideoUrl(htmlUrl);
      }
    })();
  }, [xpDataResourceUrl, videoUrl, isActive]);

  const languageSelected = location.state?.languageSelected;
  const videoName = itemByLanguage(step.name, undefined, languageSelected);

  if (!videoReference || !getStepAvailable(step)) {
    return (
      <div className="flex w-full cursor-default flex-row items-center">
        <div className="mr-2 cursor-default" data-testid="StepStatusIcon">
          <IconCircleDisabled className="text-grayscale-gray2!" />
        </div>
        <div className="pointer-events-none flex w-full cursor-default flex-col">
          <Typography data-testid="TaskTitle" variant="overline3">
            {t('Generic.video')}
          </Typography>
          <Typography data-testid="VideoName" variant="body1">
            {videoName}
          </Typography>
          <div className="text-feedback-error-base flex items-center">
            <IconAlertTriangleFilled size={16} />
            <Typography
              data-testid="MissingResourceMessage"
              variant="body1"
              className="text-feedback-error-base! ml-2!"
            >
              {isMyPlansTab
                ? t('TrainingPlans.noResource')
                : t('TrainingPlans.noResourceBuild')}
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  const renderVideoWrapper = () => {
    if (!isActive) {
      return null;
    }

    const videoSrc = tempProcessVideoSrc(videoUrl);

    if (videoSrc) {
      return (
        <div className="flex w-full justify-center">
          <BrightcovePlayer
            className="content-iframe size-full sm:min-h-60 md:w-120"
            src={videoSrc}
            title={videoName ?? 'Video'}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div>
        <div
          className={cn(
            'flex w-full flex-row items-center',
            isActive && 'pb-2.5',
          )}
        >
          {isCompleted ? (
            <button
              type="button"
              className={cn(
                'mr-2 inline-flex appearance-none border-0 bg-transparent p-0',
                !disabled ? 'cursor-pointer' : 'cursor-default',
              )}
              data-testid="StepStatusIcon"
              disabled={disabled}
              onClick={() => {
                setIsCompleted(prevState => !prevState);
                onCompleteToggle();
              }}
            >
              {!disabled ? (
                <IconCircleCheck className="text-feedback-success-base!" />
              ) : (
                <IconCircleCheckDisabled />
              )}
            </button>
          ) : (
            <button
              type="button"
              className={cn(
                'mr-2 inline-flex appearance-none border-0 bg-transparent p-0',
                !disabled ? 'cursor-pointer' : 'cursor-default',
              )}
              data-testid="StepStatusIcon"
              disabled={disabled}
              onClick={() => {
                setIsCompleted(prevState => !prevState);
                onCompleteToggle();
              }}
            >
              {!disabled ? (
                <IconCircle className="text-grayscale-gray2!" />
              ) : (
                <IconCircleDisabled />
              )}
            </button>
          )}
          <button
            type="button"
            className={cn(
              'ml-2! flex w-full flex-col text-left',
              disabled ? 'cursor-default' : 'cursor-pointer',
            )}
            disabled={disabled}
            onClick={() => onClick(step)}
          >
            <Typography data-testid="TaskTitle" variant="overline3">
              {t('Generic.video')}
              {isCompleted &&
                ` - ${t('Generic.completedIn')} ${stepDuration} ${t(
                  'Generic.mins',
                )}`}
            </Typography>
            <Typography data-testid="VideoName" variant="body1">
              {itemByLanguage(step.name, undefined, languageSelected)}
            </Typography>
          </button>
          <button
            type="button"
            className={cn(
              'text-grayscale-gray2 mx-1.5 flex w-6',
              disabled ? 'cursor-default' : 'cursor-pointer',
            )}
            disabled={disabled}
            onClick={() => onClick(step)}
          >
            <Play className="size-6!" />
          </button>
        </div>
      </div>
      {renderVideoWrapper()}
    </>
  );
};

export default PlanItemBrightcoveVideo;
